import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {compose, withProps} from 'recompose';
import {withScriptjs, withGoogleMap, GoogleMap, Marker, InfoWindow} from 'react-google-maps';
import InfoBox from "react-google-maps/lib/components/addons/InfoBox";
import googleMapsStyles from '../../assets/js/googleMapsStyles';
import MarkerIcon from '../../assets/img/marker-map.svg';
import MarkerFocusedIcon from '../../assets/img/marker-map-focused.svg';
import {setUiSchoolFocused, getSchoolsMaps} from "../../actions";
import Link from 'react-router-dom/Link';
import Button from '@material-ui/core/Button';
import IconClose from '@material-ui/icons/Close';
import ReactHtmlParser from 'react-html-parser';
import PlaceholderImage from '../../assets/img/placeholder-school-thumbnail.jpg';
import {withLocalize, Translate} from 'react-localize-redux';
import translations from '../../translations/translations_schools_list';
import classnames from 'classnames';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

class SchoolsMapComponent extends Component {
    mapRef = React.createRef();

    state = {
        bounds: null,
        mapBoundsChangedFirstLoad: false,
        centerLatitude: 46.204391, // Geneva
        centerLongitude: 6.143158, // Geneva
        infoBoxVisible: 0,
        schoolsOnMap: [],
        markerOver: 0,
    };

    constructor(props) {
        super(props);

        this.props.addTranslation(translations);
    }

    fetchContent() {
        const {loading, search} = this.props;

        if (!loading) {
            this.props.getSchoolsMaps(
                search.type,
                search.programme,
                search.degree
            );
        }
    }

    componentDidMount() {
        this.fetchContent();
    }

    componentDidUpdate(prevProps, prevState) {
        const {schools, schoolsListing} = this.props;

        if (!this.props.loading) {
            if (prevProps.languages !== this.props.languages) {
                const {pathname} = this.props.location;
                const expectedUri = this.props.translate('routes.schools');

                if (pathname !== expectedUri) {
                    this.props.history.push(expectedUri);
                } else {
                    this.fetchContent();
                }
            }

            if ((Array.isArray(schools) && schools.length > 0) && (prevProps.schools !== schools)) {
                // Prepare markers

                let schoolsOnMap = [];
                let baseUri = this.props.translate('routes.schools_details');

                schools.forEach(school => {
                    const latitude = ((school.latitude && school.latitude !== 0 && school.latitude.length > 0) ? parseFloat(school.latitude) : 0);
                    const longitude = ((school.longitude && school.longitude !== 0 && school.longitude.length > 0) ? parseFloat(school.longitude) : 0);

                    if (latitude > 0 && longitude > 0) {
                        schoolsOnMap.push({
                            id: school.id,
                            lat: latitude,
                            lng: longitude,
                            url: baseUri.replace(':slug', school.slug),
                            title: school.title,
                            thumbnail: school.thumbnail,
                        });
                    }
                });

                this.setState({
                    schoolsOnMap: schoolsOnMap,
                });

                // Recenter the map (fitBounds)

                if (this.mapRef.current) {
                    let bounds = new window.google.maps.LatLngBounds();

                    schools.forEach((school, index) => {
                        const latitude = ((school.latitude && school.latitude !== 0 && school.latitude.length > 0) ? parseFloat(school.latitude) : 0);
                        const longitude = ((school.longitude && school.longitude !== 0 && school.longitude.length > 0) ? parseFloat(school.longitude) : 0);

                        if (latitude > 0 && longitude > 0) {
                            bounds.extend(new window.google.maps.LatLng(latitude, longitude));
                        }
                    });

                    // Sets the viewport (map zoom and center) to contain all the markers
                    // This will automatically recenter the map
                    this.mapRef.current.fitBounds(bounds);
                }
            }

            if (prevProps.schoolsListing !== schoolsListing) {
                // The list of schools has changed: it happened for example if the user has filtered the results by a taxonomy.
                // We want to fetch again the content to display on the map the schools matching the user search criteria.

                if (Array.isArray(schoolsListing) && schoolsListing.length > 0) {
                    this.fetchContent();
                } else {
                    this.setState({
                        schoolsOnMap: [],
                    });
                }
            }
        }
    }

    onMarkerClick = (idSchool, lat, lng) => {
        this.setState({
            infoBoxVisible: idSchool,
        });
    };

    onMarkerMouseOver = (idSchool) => {
        if (!this.props.mobile) {
            this.setState({
                markerOver: idSchool,
            });
        }
    };

    onMarkerMouseOut = () => {
        if (!this.props.mobile) {
            this.setState({
                markerOver: 0,
            });
        }
    };

    onMapClick = () => {
        this.setState({
            infoBoxVisible: 0,
        });
    };

    onInfoBoxCloseClick = () => {
        this.setState({
            infoBoxVisible: 0,
        });
    };

    render() {
        const {idSchoolFocused, mobile} = this.props;
        const {centerLatitude, centerLongitude, infoBoxVisible, schoolsOnMap, markerOver} = this.state;

        const _this = this;

        const markerIcon = {
            url: MarkerIcon,
            scaledSize: new window.google.maps.Size(52, 52),
            labelOrigin: new window.google.maps.Point(20, 18),
        };

        const markerFocusedIcon = {
            url: MarkerFocusedIcon,
            scaledSize: new window.google.maps.Size(52, 52),
            labelOrigin: new window.google.maps.Point(20, 18),
        };

        return (
            <GoogleMap
                ref={this.mapRef}
                defaultZoom={12}
                defaultCenter={{
                    lat: centerLatitude,
                    lng: centerLongitude,
                }}
                defaultOptions={{
                    disableDefaultUI: true,
                    fullscreenControl: true,
                    zoomControl: true,
                    zoomControlOptions: {
                        position: window.google.maps.ControlPosition.RIGHT_BOTTOM
                    },
                    styles: googleMapsStyles,
                    scrollwheel: true,
                    maxZoom: 18
                }}
                onClick={this.onMapClick}
            >
                {schoolsOnMap.map((school, index) => {
                    let infoBoxThumbnailStyle = {};

                    if (school.thumbnail && school.thumbnail !== '') {
                        infoBoxThumbnailStyle = {
                            backgroundImage: 'url("' + school.thumbnail + '")',
                        };
                    } else {
                        infoBoxThumbnailStyle = {
                            backgroundImage: `url(${PlaceholderImage})`,
                        };
                    }

                    return (
                        <Marker
                            key={'marker_' + school.id}
                            visible={infoBoxVisible !== school.id}
                            position={{
                                lat: school.lat,
                                lng: school.lng,
                            }}
                            title=""
                            label={{
                                text: ' ',
                                color: 'white',
                                fontSize: '16px',
                                fontWeight: '500',
                            }}
                            icon={(school.id === markerOver || school.id === idSchoolFocused) ? markerFocusedIcon : markerIcon}
                            onClick={() => _this.onMarkerClick(school.id, school.lat, school.lng)}
                            onMouseOver={() => _this.onMarkerMouseOver(school.id)}
                            onMouseOut={() => _this.onMarkerMouseOut()}
                        >
                            <InfoBox
                                key={'infobox_' + school.id}
                                visible={infoBoxVisible === school.id}
                                options={{
                                    alignBottom: true,
                                    pixelOffset: new window.google.maps.Size(-90, 0),
                                    pane: "floatPane",
                                    boxStyle: {
                                        width: '180px',
                                        //height: '280px',
                                        height: 'auto',
                                    },
                                    closeBoxURL: ``,
                                    enableEventPropagation: mobile ? false : true,
                                    disableAutoPan: false,
                                }}
                            >
                                <div className={classnames('info-box-map', {
                                    'mobile': mobile,
                                })}>
                                    <div className="box">
                                        <div className="thumbnail" style={infoBoxThumbnailStyle}></div>
                                        {mobile ? (
                                            <a className="content" href={school.url}>
                                                <span className="title">{ReactHtmlParser(school.title)}</span>
                                                <Button variant="contained" color="primary" size="small" className="btn-view" component={props => <Link to={school.url} {...props}/>}>
                                                    {this.props.translate('schools_list.map_infobox_discover_button')}
                                                </Button>
                                            </a>
                                        ) : (
                                            <div className="content">
                                                <span className="title">{ReactHtmlParser(school.title)}</span>
                                                <Button variant="contained" color="primary" size="small" className="btn-view" component={props => <Link to={school.url} {...props}/>}>
                                                    {this.props.translate('schools_list.map_infobox_discover_button')}
                                                </Button>
                                            </div>
                                        )}
                                        <div className="arrow"></div>
                                        <div className="close" onClick={_this.onInfoBoxCloseClick}>
                                            <IconClose className="icon"/>
                                        </div>
                                    </div>
                                </div>
                            </InfoBox>
                        </Marker>
                    );
                })}

            </GoogleMap>
        );
    }
}

SchoolsMapComponent.propTypes = {
    setUiSchoolFocused: PropTypes.func.isRequired,
    getSchoolsMaps: PropTypes.func.isRequired,
    idSchoolFocused: PropTypes.number.isRequired,
    translate: PropTypes.func.isRequired,
    mobile: PropTypes.bool
};

SchoolsMapComponent.defaultProps = {
    mobile: false
};

const mapStateToProps = (state) => ({
    schools: state.schools.schoolsMaps,
    schoolsListing: state.schools.schools,
    loading: state.schools.schoolsMapsLoading,
    idSchoolFocused: state.schools.idSchoolFocused,
    search: state.schools.search,
});

const SchoolsMap = compose(
    withRouter,
    connect(mapStateToProps, {setUiSchoolFocused, getSchoolsMaps}),
    withProps({
        googleMapURL: "https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=" + GOOGLE_MAPS_API_KEY,
        loadingElement: <div style={{height: `100%`}}/>,
        containerElement: <div className="mapContainer" style={{height: `100%`}}/>,
        mapElement: <div style={{height: `100%`}}/>,
    }),
    withScriptjs,
    withGoogleMap,
    withLocalize
)(SchoolsMapComponent);

export default SchoolsMap;


// WEBPACK FOOTER //
// ./src/features/Schools/components/List/SchoolsMap.js